// import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { UserService } from 'src/services/api/UserService';
import { ResumeService } from 'src/services/api/ResumeService';
import {
  getRefreshToken,
  setToken,
  setRefreshToken,
  setUser,
  clearAuth,
} from 'src/utils/authStorage';
import { setGaUserId, trackEvent } from 'src/utils/analytics';
import { useLogoutUser } from './useUserService';
// import { useAuth } from './useAuth';

export const useAuthActions = () => {
  const navigate = useNavigate();
  const logoutMutation = useLogoutUser();
  // const { login: setAuthUser, logout: clearAuthUser } = useAuth();
  const consumePostAuthRedirectPath = () => {
    try {
      const redirectPath = sessionStorage.getItem('postAuthRedirectPath');
      if (redirectPath && redirectPath.startsWith('/')) {
        sessionStorage.removeItem('postAuthRedirectPath');
        return redirectPath;
      }
    } catch (_) {
      // ignore session storage errors
    }
    return null;
  };

  const handleAuthError = (error) => {
    console.error('Auth error details:', {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    });

    if (error.response) {
      switch (error.response.status) {
        case 401:
          throw new Error('Invalid credentials');
        case 403:
          throw new Error('Account is locked. Please contact support');
        case 404:
          throw new Error('Account not found');
        case 422:
          throw new Error('Invalid login credentials');
        case 429:
          throw new Error('Too many login attempts. Please try again later');
        default:
          throw new Error(
            error.response.data?.message || 'An error occurred during login'
          );
      }
    } else if (error.request) {
      throw new Error(
        'Unable to connect to the server. Please check your internet connection'
      );
    }
    throw new Error('An unexpected error occurred');
  };

  const logout = async () => {
    console.debug('[auth] logout:start', {
      bodyPointerEvents: document?.body?.style?.pointerEvents,
      bodyOverflow: document?.body?.style?.overflow,
      activeElement: document?.activeElement?.tagName,
    });

    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        const logoutData = {
          refresh_token: refreshToken,
        };
        // Use React Query mutation for logout
        await logoutMutation.mutateAsync(logoutData);
      } catch (error) {
        console.error('Logout failed:', error);
        throw handleAuthError(error);
      }
    }

    // Clear auth data from localStorage (shared across tabs)
    clearAuth();

    // Clear non-auth data from localStorage (shared across tabs)
    // These are app-level settings that should be cleared on logout
    try {
      localStorage.removeItem('pendingSecondaryLink');
      localStorage.removeItem('pendingSecondaryLinkReturn');
      // Clear dynamic keys (processing deadline keys)
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith('submit_resume_processing_deadline_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear localStorage items:', error);
    }

    // Defensive cleanup: if a modal/sheet left body styles behind, clear them.
    // This should be harmless even when not needed, and helps diagnose "page is unclickable" issues.
    try {
      if (document?.body?.style?.pointerEvents === 'none') {
        console.warn('[auth] clearing stale body.pointerEvents=none');
        document.body.style.pointerEvents = '';
      }
      if (document?.body?.style?.overflow === 'hidden') {
        console.warn('[auth] clearing stale body.overflow=hidden');
        document.body.style.overflow = '';
      }
    } catch (_) {
      // ignore
    }

    console.debug('[auth] logout:navigate:/join-us', {
      bodyPointerEvents: document?.body?.style?.pointerEvents,
      bodyOverflow: document?.body?.style?.overflow,
    });
    navigate('/join-us');
  };
  // const getCidFromStorage = () => {
  //   try {
  //     const userData = localStorage.getItem('userData');
  //     if (!userData) return '0';

  //     const parsedData = JSON.parse(userData);
  //     return parsedData?.cid || '0';
  //   } catch (error) {
  //     console.error('Error getting CID from storage:', error);
  //     return '0';
  //   }
  // };

  const processLoginResponse = async (responseData) => {
    // Clear auth data from localStorage before setting new session data
    clearAuth();

    const tokens = responseData?.tokens;

    if (tokens?.access_token) {
      // Store tokens in localStorage (shared across tabs)
      setToken(tokens.access_token);
      if (tokens.refresh_token) {
        setRefreshToken(tokens.refresh_token);
      }

      let decodedUser;
      try {
        decodedUser = jwtDecode(tokens.access_token);
      } catch (error) {
        console.error('Failed to decode access token:', error);
        throw new Error('Invalid token received');
      }

      const userData = {
        cid: decodedUser?.cid,
        email: decodedUser?.email,
        firstName: decodedUser?.given_name,
        lastName: decodedUser?.family_name,
        emailVerified: decodedUser?.email_verified,
        roles: decodedUser?.realm_access?.roles,
        secAuth: decodedUser?.secAuth,
      };

      // Set user in auth context
      // setAuthUser(userData);

      // Store user data in localStorage
      try {
        setUser(userData);
      } catch (storageError) {
        console.error('Failed to persist user (login flow):', storageError);
      }
      if (userData.cid) setGaUserId(userData.cid);

      const postAuthRedirectPath = consumePostAuthRedirectPath();
      if (postAuthRedirectPath) {
        navigate(postAuthRedirectPath, { replace: true });
        return responseData;
      }

      navigate('/profile-dashboard');

      return responseData;
    }

    throw new Error('Invalid login response received');
  };

  const login = async (credentials) => {
    try {
      // Clear any existing auth state before attempting new login
      await logout();

      // Use React Query mutation
      const response = await UserService.loginUser(credentials);
      // Process the login response
      const responseData = response?.data || response;
      return processLoginResponse(responseData);
    } catch (error) {
      console.error('Login failed:', error);
      throw handleAuthError(error);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await UserService.googleAuth();

      const authUrl = response?.auth_url || response?.data?.auth_url;
      if (authUrl) {
        // Redirect to Google login
        window.location.href = authUrl;
        return true;
      }

      throw new Error('Failed to get Google authentication URL');
    } catch (error) {
      console.error('Google login initialization failed:', error);
      throw handleAuthError(error);
    }
  };

  const handleGoogleCallback = async (code, state) => {
    try {
      // Clear any existing auth state before attempting Google login
      clearAuth();

      // Call Google callback API
      // const cid = getCidFromStorage();
      const response = await UserService.googleAuthCallback({
        code,
        state,
      });
      // Process the login response
      return processLoginResponse(response.data);
    } catch (error) {
      console.error('Google callback failed:', error);
      throw handleAuthError(error);
    }
  };

  const handleGoogleRedirectToken = async (token, refreshToken) => {
    try {
      // Clear existing auth state before setting new tokens
      clearAuth();

      const responseData = {
        tokens: {
          access_token: token,
          refresh_token: refreshToken,
        },
      };

      const result = await processLoginResponse(responseData);
      trackEvent('form_success', 'Login', 'google');
      return result;
    } catch (error) {
      console.error('Google redirect token handling failed:', error);
      throw handleAuthError(error);
    }
  };

  return {
    login,
    logout,
    handleGoogleLogin,
    handleGoogleCallback,
    handleGoogleRedirectToken,
  };
};
